import { useEffect, useMemo, useState } from 'react';
import { AppSelect } from '@/components/shared/AppSelect';
import { Badge } from '@/components/shared/Badge';
import { Button } from '@/components/shared/Button';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { Panel } from '@/components/shared/Panel';
import { WeaponIcon } from '@/components/shared/WeaponIcon';
import { roleCategoryOptions } from '@/data/options';
import type { WeaponCatalogDeleteResult, WeaponCatalogPayload } from '@/lib/repositories/adminRepository';
import type { WeaponCatalogItem } from '@/types/domain';

interface WeaponManagementPanelProps {
  weapons: WeaponCatalogItem[];
  onSave: (payload: WeaponCatalogPayload) => Promise<void>;
  onDelete: (weaponId: string, confirmationText: string) => Promise<WeaponCatalogDeleteResult | undefined>;
  onSyncBundledCatalog: () => Promise<void>;
  bundledCatalogCount: number;
}

function createEmptyWeaponDraft(): WeaponCatalogPayload {
  return {
    filename: '',
    displayName: '',
    roleCategory: null,
    imagePath: '',
    isActive: true,
    notes: '',
  };
}

export function WeaponManagementPanel({
  weapons,
  onSave,
  onDelete,
  onSyncBundledCatalog,
  bundledCatalogCount,
}: WeaponManagementPanelProps): JSX.Element {
  const sortedWeapons = useMemo(() => [...weapons].sort((left, right) => left.displayName.localeCompare(right.displayName)), [weapons]);
  const [selectedWeaponId, setSelectedWeaponId] = useState<string>('new');
  const [draft, setDraft] = useState<WeaponCatalogPayload>(createEmptyWeaponDraft());
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSyncingCatalog, setIsSyncingCatalog] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const selectedWeapon = sortedWeapons.find((weapon) => weapon.id === selectedWeaponId);

  useEffect(() => {
    if (!selectedWeapon) {
      setDraft(createEmptyWeaponDraft());
      setSelectedFile(null);
      setIsDeleteDialogOpen(false);
      return;
    }

    setDraft({
      id: selectedWeapon.id,
      filename: selectedWeapon.filename,
      displayName: selectedWeapon.displayName,
      roleCategory: selectedWeapon.roleCategory,
      imagePath: selectedWeapon.imagePath,
      storageObjectPath: selectedWeapon.storageObjectPath,
      isActive: selectedWeapon.isActive,
      notes: selectedWeapon.notes ?? '',
    });
    setSelectedFile(null);
    setIsDeleteDialogOpen(false);
  }, [selectedWeapon]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(draft.imagePath);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [draft.imagePath, selectedFile]);

  return (
    <Panel
      title="Weapon Catalog Management"
      description="Manage weapon names, role categories, active state, and icon uploads."
      action={
        <Button
          variant="secondary"
          onClick={async () => {
            setIsSyncingCatalog(true);

            try {
              await onSyncBundledCatalog();
            } finally {
              setIsSyncingCatalog(false);
            }
          }}
          disabled={isSyncingCatalog || isSaving || isDeleting}
        >
          {isSyncingCatalog ? 'Syncing...' : `Sync Albion Icons (${bundledCatalogCount})`}
        </Button>
      }
    >
      <div className="management-layout">
        <div className="management-list">
          <button
            type="button"
            className={selectedWeaponId === 'new' ? 'management-list__item management-list__item--active' : 'management-list__item'}
            onClick={() => setSelectedWeaponId('new')}
            >
              <div>
                <strong>Add New Weapon</strong>
                <span>Create a new catalog entry or upload a custom icon</span>
              </div>
            </button>

          {sortedWeapons.map((weapon) => (
            <button
              key={weapon.id}
              type="button"
              className={weapon.id === selectedWeaponId ? 'management-list__item management-list__item--active' : 'management-list__item'}
              onClick={() => setSelectedWeaponId(weapon.id)}
            >
              <div className="management-list__weapon">
                <WeaponIcon imagePath={weapon.imagePath} label={weapon.displayName} size="sm" />
                <div>
                  <strong>{weapon.displayName}</strong>
                  <span>{weapon.roleCategory ?? 'Needs review'}</span>
                </div>
              </div>
              <div className="management-list__meta">
                <Badge tone={weapon.isActive ? 'success' : 'default'}>{weapon.isActive ? 'active' : 'inactive'}</Badge>
                <span>{weapon.notes?.trim() ? 'Has notes' : 'Catalog entry'}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="management-editor">
          <label className="field">
            <span className="field__label">Weapon Display Name</span>
            <input
              className="field__input"
              value={draft.displayName}
              onChange={(event) => setDraft((current) => ({ ...current, displayName: event.target.value }))}
              placeholder="Hallowfall"
            />
          </label>

          <div className="form-grid form-grid--two">
            <div className="field">
              <span className="field__label">Role Category</span>
              <AppSelect
                ariaLabel="Weapon role category"
                value={draft.roleCategory ?? ''}
                onChange={(value) =>
                  setDraft((current) => ({
                    ...current,
                    roleCategory: value ? (value as WeaponCatalogPayload['roleCategory']) : null,
                  }))
                }
                options={[
                  { value: '', label: 'Unassigned', description: 'Keep this weapon out of role filters until reviewed' },
                  ...roleCategoryOptions.map((category) => ({
                    value: category,
                    label: category,
                    description: 'Official Woof Woof role category',
                  })),
                ]}
                placeholder="Choose a role category"
              />
            </div>

            <div className="field field--checkbox">
              <span className="field__label">Catalog State</span>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(event) => setDraft((current) => ({ ...current, isActive: event.target.checked }))}
                />
                <span>{draft.isActive ? 'Active' : 'Inactive'}</span>
              </label>
            </div>
          </div>

          <label className="field">
            <span className="field__label">Weapon Icon Upload</span>
            <input
              className="field__input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
            />
          </label>

          <label className="field">
            <span className="field__label">Notes</span>
            <textarea
              className="field__input field__input--textarea"
              value={draft.notes ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
              placeholder="Optional notes for later catalog refinement"
            />
          </label>

          {previewUrl ? (
            <div className="weapon-preview">
              <WeaponIcon imagePath={previewUrl} label={draft.displayName || 'Weapon preview'} size="lg" />
              <div>
                <span className="profile-summary__label">Current Icon</span>
                <strong>{draft.displayName || 'Weapon preview'}</strong>
                <span className="profile-summary__hint">
                  {selectedFile ? `Pending upload: ${selectedFile.name}` : selectedWeapon ? 'Using saved catalog icon' : 'Upload an icon to complete this entry'}
                </span>
              </div>
            </div>
          ) : null}

          <div className="form-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedWeaponId('new');
                setDraft(createEmptyWeaponDraft());
                setSelectedFile(null);
              }}
            >
              New Weapon
            </Button>
            <Button
              onClick={async () => {
                setIsSaving(true);

                try {
                  await onSave({ ...draft, uploadFile: selectedFile });
                  if (!draft.id) {
                    setSelectedWeaponId('new');
                    setDraft(createEmptyWeaponDraft());
                  }
                  setSelectedFile(null);
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : draft.id ? 'Save Weapon' : 'Add Weapon'}
            </Button>
            {selectedWeapon ? (
              <Button
                variant="danger"
                onClick={() => {
                  setDeleteConfirmation('');
                  setIsDeleteDialogOpen(true);
                }}
                disabled={isSaving || isDeleting}
              >
                Delete Weapon
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={Boolean(selectedWeapon && isDeleteDialogOpen)}
        title="Delete Weapon"
        description="Type DELETE to confirm. If this weapon is already referenced by templates or masses, Woof Woof will set it inactive instead of removing the catalog record."
        confirmLabel="Delete Weapon"
        confirmVariant="danger"
        confirmationLabel="Type DELETE to confirm"
        requiredText={selectedWeapon ? 'DELETE' : undefined}
        confirmationValue={deleteConfirmation}
        onConfirmationValueChange={setDeleteConfirmation}
        isProcessing={isDeleting}
        onClose={() => {
          if (isDeleting) {
            return;
          }

          setIsDeleteDialogOpen(false);
          setDeleteConfirmation('');
        }}
        onConfirm={() => {
          if (!selectedWeapon) {
            return;
          }

          setIsDeleting(true);

          void onDelete(selectedWeapon.id, deleteConfirmation).then((result) => {
            if (!result) {
              return;
            }

            if (result.result === 'deleted') {
              setSelectedWeaponId('new');
              setDraft(createEmptyWeaponDraft());
              setSelectedFile(null);
            }

            setIsDeleteDialogOpen(false);
            setDeleteConfirmation('');
          }).finally(() => {
            setIsDeleting(false);
          });
        }}
      />
    </Panel>
  );
}
